import { DollarSign, Zap, Clock, Car, Calendar, Receipt } from 'lucide-react';
import { InfoSection, InfoField } from '@/core/components/ui/detail-blade';
import { Separator } from '@/core/components/ui/separator';
import { KioskSessionGridRow, KioskSessionDetail } from '../types/kiosk-session-types';

interface CostBreakdownSectionProps {
  session: KioskSessionGridRow | null;
  detail: KioskSessionDetail | undefined;
  fallbackCurrency?: string;
}

function isValidCurrencyCode(code: string | undefined): code is string {
  if (!code || !/^[A-Z]{3}$/.test(code)) return false;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: code });
    return true;
  } catch { return false; }
}

export function CostBreakdownSection({ session, detail, fallbackCurrency }: CostBreakdownSectionProps) {
  if (!detail) {
    return null;
  }

  // Check if we have any cost breakdown data or VAT data
  const hasCostBreakdown =
    detail.fixed_cost != null ||
    detail.energy_cost != null ||
    detail.time_cost != null ||
    detail.parking_cost != null ||
    detail.reservation_cost != null;

  const hasVatBreakdown =
    detail.totalIncVat != null ||
    detail.totalExcVat != null ||
    detail.vatAmount != null;

  // If no cost breakdown or VAT data, don't render the section
  if (!hasCostBreakdown && !hasVatBreakdown) {
    return null;
  }

  const formatCurrency = (value: number | undefined, currency?: string) => {
    if (value == null) return 'N/A';
    const rawCode = currency || detail.currency;
    const safeCurrency = isValidCurrencyCode(rawCode) ? rawCode
      : isValidCurrencyCode(fallbackCurrency) ? fallbackCurrency
      : null;
    if (!safeCurrency) {
      return new Intl.NumberFormat('en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    return new Intl.NumberFormat('en-IL', {
      style: 'currency',
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Calculate total from components if available
  const calculateTotal = () => {
    const components = [
      detail.fixed_cost,
      detail.energy_cost,
      detail.time_cost,
      detail.parking_cost,
      detail.reservation_cost
    ].filter((v): v is number => v != null);

    if (components.length === 0) return detail.settlement_value;
    return components.reduce((sum, val) => sum + val, 0);
  };

  return (
    <InfoSection
      title="Cost Breakdown"
      icon={DollarSign}
      collapsible={true}
      defaultExpanded={true}
      storageKey="kioskSessionDetail.costBreakdown.expanded"
    >
      {/* Cost Components */}
      {hasCostBreakdown && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            {detail.energy_cost != null && (
              <InfoField
                label="Energy Cost"
                value={formatCurrency(detail.energy_cost)}
                icon={Zap}
              />
            )}

            {detail.time_cost != null && (
              <InfoField
                label="Time Cost"
                value={formatCurrency(detail.time_cost)}
                icon={Clock}
              />
            )}

            {detail.parking_cost != null && (
              <InfoField
                label="Parking Cost"
                value={formatCurrency(detail.parking_cost)}
                icon={Car}
              />
            )}
          </div>

          <div className="space-y-3">
            {detail.fixed_cost != null && (
              <InfoField
                label="Fixed Fee"
                value={formatCurrency(detail.fixed_cost)}
                icon={DollarSign}
              />
            )}

            {detail.reservation_cost != null && (
              <InfoField
                label="Reservation Cost"
                value={formatCurrency(detail.reservation_cost)}
                icon={Calendar}
              />
            )}

            <InfoField
              label="Total Cost"
              value={formatCurrency(calculateTotal())}
              icon={DollarSign}
              className="font-semibold text-primary"
            />
          </div>
        </div>
      )}

      {/* VAT Breakdown Section */}
      {hasVatBreakdown && (
        <>
          {hasCostBreakdown && <Separator className="my-4" />}

          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Calculated Charging Cost (VAT Breakdown)
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {detail.totalExcVat != null && (
                <InfoField
                  label="Total exc VAT"
                  value={formatCurrency(detail.totalExcVat)}
                />
              )}

              {detail.vatAmount != null && (
                <InfoField
                  label="VAT Amount"
                  value={formatCurrency(detail.vatAmount)}
                />
              )}

              {detail.totalIncVat != null && (
                <InfoField
                  label="Total inc VAT"
                  value={formatCurrency(detail.totalIncVat)}
                  className="font-semibold"
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Currency indicator */}
      {(isValidCurrencyCode(detail.currency) || isValidCurrencyCode(fallbackCurrency)) && (
        <div className="mt-3 text-xs text-muted-foreground">
          All amounts in {isValidCurrencyCode(detail.currency) ? detail.currency : fallbackCurrency}
        </div>
      )}
    </InfoSection>
  );
}
